import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const InvestorAuthContext = createContext(null);

export function InvestorAuthProvider({ children }) {
  const [investor, setInvestor] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('doorly_investor_token'));
  const [investorLoading, setInvestorLoading] = useState(true);

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setInvestor(payload);
        } else {
          localStorage.removeItem('doorly_investor_token');
          setToken(null);
          setInvestor(null);
        }
      } catch {
        localStorage.removeItem('doorly_investor_token');
        setToken(null);
        setInvestor(null);
      }
    }
    setInvestorLoading(false);
  }, [token]);

  // Memoize the action callbacks. Without useCallback they get fresh refs
  // on every provider render, which trips the [token, investorLogout,
  // refreshInvestor] dependency array in MarketDashboardInvestor's effect
  // — refreshInvestor() inside the effect re-renders the provider, which
  // hands a new ref back, which re-fires the effect, ad infinitum, and
  // setLoading(false) never lands → the page is stuck on "Loading…".
  const investorLogin = useCallback((newToken, investorData) => {
    localStorage.setItem('doorly_investor_token', newToken);
    setToken(newToken);
    if (investorData) {
      setInvestor(investorData);
    } else {
      try {
        const payload = JSON.parse(atob(newToken.split('.')[1]));
        setInvestor(payload);
      } catch {
        setInvestor(null);
      }
    }
  }, []);

  const investorLogout = useCallback(() => {
    localStorage.removeItem('doorly_investor_token');
    setToken(null);
    setInvestor(null);
  }, []);

  const refreshInvestor = useCallback((data) => setInvestor(data), []);

  return (
    <InvestorAuthContext.Provider
      value={{ investor, token, investorLogin, investorLogout, investorLoading, refreshInvestor }}
    >
      {children}
    </InvestorAuthContext.Provider>
  );
}

export const useInvestorAuth = () => useContext(InvestorAuthContext);
